import { inject } from '@angular/core';
import AsyncLock from 'async-lock';
import { IDBPDatabase, IDBPTransaction, openDB, StoreNames } from 'idb';
import { AppContext } from 'src/app/app/context';
import { logger } from 'src/app/pbir/utils/logger';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { VisualDataRequest } from '../../VisualDataRequest';
import { VisualDataQueryStatus } from '../../VisualDataSource';
import { DataQueryCache } from './DataQueryCache';
import { QueryState } from './QueryImpl';
import { getUniqueTabId } from './utils';

const LOGGER = logger('IndexedDBDataQueryCache');

const defaultQueryExpiryMs = 1000 * 60 * 15;
const timeFromLastUpdateToExpireMs = 1000 * 10;
const tabPingPongTimeoutMs = 1000;

export class IndexedDBDataQueryCache extends DataQueryCache {
  private readonly appContext = inject(AppContext);
  private readonly organisationId = this.appContext.user().organisationId;
  private readonly userId = this.appContext.user().id;

  private readonly lock = new AsyncLock({
    timeout: 1000 * 15
  });

  private readonly tabId: string;

  constructor() {
    super();
    this.tabId = getUniqueTabId('Insights:IndexedDBDataQueryCache:TabId');
    this.initDbEventBroadcastChannel();
  }

  async getOrAddQuery(request: VisualDataRequest): Promise<DataQueryCache.CachedQuery> {
    let { status, owned, table } = await this.getOrUpdateRequest(request, null, defaultQueryExpiryMs);
    return { status, owned, state: table.state };
  }

  async tryUpdateQuery(request: VisualDataRequest, queryState: QueryState): Promise<DataQueryCache.CachedQuery> {
    let { status, owned, table } = await this.getOrUpdateRequest(request, queryState, defaultQueryExpiryMs);
    return { status, owned, state: table.state };
  }

  async hasQuery(request: VisualDataRequest): Promise<boolean> {
    return this._isValid<DbRequest>(
      DbRequest.table,
      DbRequest.byRequestCacheKey,
      request.cacheKey,
      table => this.isRequestExpired(table));
  }

  async removeQuery(request: VisualDataRequest): Promise<void> {
    await this.expireRequest(request);
  }

  async getOrAddQueryResource(queryId: DataQueryCache.CachedQueryId, key: any): Promise<any> {
  }

  private async getOrUpdateRequest(
    request: VisualDataRequest,
    queryState?: QueryState,
    expiryInMs?: number
  ) {
    return await this._getOrUpdate<DbRequest>(
      DbRequest.table,
      DbRequest.byRequestCacheKey,
      request.cacheKey,
      expiryInMs,
      table => this.isRequestExpired(table),
      table => {
        table.requestCacheKey = request.cacheKey;

        if (queryState)
          table.state = queryState;
      });
  }

  private async expireRequest(request: VisualDataRequest) {
    return await this._getOrUpdate<DbRequest>(
      DbRequest.table,
      DbRequest.byRequestCacheKey,
      request.cacheKey,
      0,
      table => true);
  }

  private isRequestExpired(request: DbRequest) {
    let currentTime = new Date().getTime();

    if (!isNullOrUndefined(request.expiry) && currentTime >= request.expiry)
      return true;

    if (!request.state || (request.state.status < VisualDataQueryStatus.Success)) {
      let expireAfter = request.lastUpdated + timeFromLastUpdateToExpireMs;
      if (isNullOrUndefined(request.lastUpdated) || currentTime >= expireAfter)
        return true;
    }

    return request.state?.status == VisualDataQueryStatus.Failure;
  }

  private async _isValid<T extends DbTable>(
    storeName: string,
    indexName: string,
    indexValue: any,
    isExpired: (table: T) => boolean
  ) {
    let ownerTabId = await this.getTabId(storeName, indexName, indexValue);
    if (!ownerTabId)
      return false;

    return await this.lock.acquire(
      this.idpDatabaseName,
      async () => {
        return await this.runWithIdbTransaction(async (tx, db) => {
          let store = tx.objectStore(storeName);
          let table = <T>await store.index(indexName).get(indexValue);
          if (table) {
            if (!isExpired(table)) {
              return true;
            }
          }

          return false;
        }, 'readonly');
      }
    );
  }

  private async _getOrUpdate<T extends DbTable>(
    storeName: string,
    indexName: string,
    indexValue: any,
    expiryInMs: number,
    isExpired: (table: T) => boolean,
    update?: (table: T) => void
  ) {
    let status = DataQueryCache.CachedQueryStatus.Returned;

    let replace = false;

    let ownerTabId = await this.getTabId(storeName, indexName, indexValue);
    if (!ownerTabId) {
      replace = true;
    }

    let owned = !ownerTabId || this.tabId == ownerTabId;

    let expired = false;
    let newExpiry = !isNullOrUndefined(expiryInMs) ? (new Date().getTime() + expiryInMs) : null;

    let table = await this.lock.acquire(
      this.idpDatabaseName,
      async () => {
        return await this.runWithIdbTransaction(async (tx, db) => {
          let store = tx.objectStore(storeName);
          let table = <T>await store.index(indexName).get(indexValue);
          if (!table) {
            table = <T>{};
            replace = true;
            status = DataQueryCache.CachedQueryStatus.AddedNew;
          }
          else {
            expired = isExpired(table);

            if (owned || expired) {
              replace = true;

              if (expired)
                status = DataQueryCache.CachedQueryStatus.ReplacedExpired;
              else
                status = DataQueryCache.CachedQueryStatus.Updated;
            }
          }

          if (replace) {
            table.tabId = this.tabId;

            if (update) {
              expired = false;
              table.expiry = newExpiry;
              table.lastUpdated = new Date().getTime();
              update(table);
            }
          }

          table.lastUpdated = new Date().getTime();
          await store.put(table);

          return table;
        }, 'readwrite');
      }
    );

    return { status, owned, table };
  }

  private async getTabId<T extends DbTable>(
    storeName: string,
    indexName: string,
    indexValue: any
  ): Promise<string | null> {
    let tabId = await this.runWithIdbTransaction(async (tx, db) => {
      let store = tx.objectStore(storeName);
      let value = <T>await store.index(indexName).get(indexValue);
      return value?.tabId;
    }, 'readonly');

    if (tabId == this.tabId)
      return tabId;

    if (tabId) {
      if (await this.pingTab(tabId, [storeName, indexName, indexValue].join(',')))
        return tabId;
    }

    return null;
  }

  private async runWithIdbTransaction<R, M extends IDBTransactionMode>(
    callback: RunWithIdbTransactionCallback<R, M>, mode: M): Promise<R> {
    let db = await this.getIdbDatabase();

    try {
      let tx = db.transaction([DbRequest.table, DbResource.table, DbChunk.table], mode);
      try {
        let result = await callback(tx, db);
        tx.commit();
        return result;
      }
      catch (err) {
        tx.abort();
        throw err;
      }
    }
    finally {
      db.close();
    }
  }

  private readonly idpDatabaseName = `IndexedDBDataQueryCache:${this.organisationId}:${this.userId}`;
  private readonly idpDatabaseVersion = 1;

  private idbDatabase: IDBPDatabase<unknown>;

  private async getIdbDatabase() {
    return await openDB(this.idpDatabaseName, this.idpDatabaseVersion, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(DbRequest.table)) {
          let dbRequests = db.createObjectStore(DbRequest.table, {
            keyPath: 'requestId',
            autoIncrement: true
          });

          dbRequests.createIndex('byRequestCacheKey', 'requestCacheKey', { unique: true });
        }

        if (!db.objectStoreNames.contains(DbResource.table)) {
          let dbResources = db.createObjectStore(DbResource.table, {
            keyPath: 'resourceId',
            autoIncrement: true
          });

          dbResources.createIndex('byQueryId', 'queryId', { unique: false });
          dbResources.createIndex('byResourceCacheKey', 'resourceCacheKey', { unique: true });
        }


        if (!db.objectStoreNames.contains(DbChunk.table)) {
          let dbChunks = db.createObjectStore(DbChunk.table, {
            keyPath: 'chunkId',
            autoIncrement: true
          });

          dbChunks.createIndex('byResourceId', 'resourceId', { unique: false });
          dbChunks.createIndex('byChunkCacheKey', 'chunkCacheKey', { unique: true });
        }
      }
    });
  }


  private dbEventBroadcastChannel: BroadcastChannel;

  private initDbEventBroadcastChannel() {
    this.dbEventBroadcastChannel = new BroadcastChannel(DbEvents.channel);
    this.dbEventBroadcastChannel.addEventListener('message', e => {
      let eventData = e.data;
      if (eventData && 'type' in eventData) {
        if (eventData.type == DbEvents.TabPing.name) {
          this.processPingTab(eventData);
          return;
        }

        if (eventData.type == DbEvents.TabPingResponse.name) {
          this.processTabPingResponse(eventData);
          return;
        }
      }

      LOGGER.error(`Invalid DbEvent data`, { e });
    });
  }

  private pingTab(tabId: string, about: string, timeout?: number): Promise<boolean> {
    this.dbEventBroadcastChannel.postMessage({
      type: DbEvents.TabPing.name,
      requestedBy: this.tabId,
      about,
      tabId
    });

    timeout ??= tabPingPongTimeoutMs;
    let endTime = new Date().getTime() + timeout;

    return new Promise((resolve, reject) => {
      let wrapperResolve = (b: boolean) => {
        clearInterval(interval);
        resolve(b);
      };

      this.waitForResponseFrom.push({
        tabId,
        about,
        resolve: wrapperResolve
      });

      let interval = setInterval(() => {
        if (new Date().getTime() >= endTime) {
          wrapperResolve(false);
        }
      }, 20);
    });
  }

  private repondToPingTab(requestedBy: string, about: string) {
    this.dbEventBroadcastChannel.postMessage({
      type: DbEvents.TabPingResponse.name,
      requestedBy,
      about,
      tabId: this.tabId
    });
  }


  private waitForResponseFrom: {
    tabId: string;
    about: any;
    resolve: (b: boolean) => void;
  }[] = [];

  private processPingTab(e: DbEvents.TabPing) {
    if (e.requestedBy == this.tabId)
      return;

    this.repondToPingTab(e.requestedBy, e.about);
  }


  private processTabPingResponse(e: DbEvents.TabPingResponse) {
    if (e.requestedBy != this.tabId)
      return;

    let remaining = [];

    for (let x of this.waitForResponseFrom) {
      if (e.tabId == x.tabId && e.about == x.about) {
        try {
          x.resolve(true);
        }
        catch (err) {
          LOGGER.error(err);
        }
      }
      else {
        remaining.push(x);
      }
    }

    this.waitForResponseFrom = remaining;
  }
}

namespace DbEvents {
  export const channel = 'Insights:IndexedDBDataQueryCache';


  export interface TabPing {
    readonly type: typeof TabPing.name;
    readonly requestedBy: string;
    readonly tabId: string;
    readonly about: string;
  }

  export namespace TabPing {
    export const name = 'Insights:IndexedDBDataQueryCache:TabPing';
  }


  export interface TabPingResponse {
    readonly type: typeof TabPingResponse.name;
    readonly requestedBy: string;
    readonly tabId: string;
    readonly about: string;
  }

  export namespace TabPingResponse {
    export const name = 'Insights:IndexedDBDataQueryCache:TabPingResponse';
  }
}

interface DbTable {
  tabId: string;
  expiry: number;
  lastUpdated: number;
}

interface DbRequest extends DbTable {
  requestId: number;
  requestCacheKey: string;
  state: QueryState;
}

namespace DbRequest {
  export const table = 'DbRequest';
  export const byRequestCacheKey = 'byRequestCacheKey';
}


interface DbResource extends DbTable {
  requestId: number;
  resourceId: number;
  resourceCacheKey: [number, string]; // [requestId, resourceCacheKey]
}

namespace DbResource {
  export const table = 'DbResource';
  export const byResourceCacheKey = 'byResourceCacheKey';
}


interface DbChunk extends DbTable {
  resourceId: number;
  chunkId: number;
  chunkCacheKey: [number, number]; // [resourceId, chunkIndex]

  chunkIndex: number
  rowOffset: number;
  rows: ReadonlyArray<ReadonlyArray<any>>;
}

namespace DbChunk {
  export const table = 'DbChunk';
  export const byChunkCacheKey = 'byChunkCacheKey';
}

type RunWithIdbTransactionCallback<R, M extends IDBTransactionMode> =
  (tx: IDBPTransaction<unknown, ArrayLike<StoreNames<unknown>>, M>, db: IDBPDatabase<unknown>) => Promise<R>;
